import { PropsWithChildren, useEffect, useState } from "react";
import {
  BlueBirdTheme,
  Button, Divider, Form, Loading, Typography,
  useSnackbar,
} from "@kitbird/core";
import { useQuery } from "react-query";
import Modal from "../../../../../components/Modal";
import {
  byteStringToFileSize, isHtmlString, toRupiah, toStringDate,
} from "../../../../../utils/common";
import { FormGroup, FormWrapper } from "../../../../../components/Form/Form.style";
import useCreateReceiptSubmission from "../../hooks/useCreateReceiptSubmission";
import { ApprovalModalType } from "../../enums";
import { IGetDetailTrxResponse, IGetTrxData, deleteTrx } from "../../../../../services/trxService";
import { DescriptionCol, FileItem, FileWrapper } from "../styles";
import { ReactComponent as PdfIcon } from "../../../../../assets/icons/pdf_file.svg";
import { ReactComponent as ImgIcon } from "../../../../../assets/icons/img_icon.svg";
import { ReactComponent as ShowIcon } from "../../../../../assets/icons/show_icon.svg";
import { getDocUrl } from "../../../../../services/bbOneGcsService";
import { putTrxApproval } from "../../../../../services/trxApprovalService";
import LabelAndValue from "../LabelAndValue";
import { CommonTrxTypes } from "../../../../../enums/common";
import useAuth from "../../../../../components/Authentication/useAuth";
import { getAreas, IAreaData } from "../../../../../services/areaService";

const {
  danger, gray, primary, secondary,
} = BlueBirdTheme.colors;

interface ReceiptSubmissionApprovalModalI extends PropsWithChildren {
  isApprovalModal: ApprovalModalType | false,
  setApprovalModal: React.Dispatch<React.SetStateAction<ApprovalModalType | false>>,
  detailRow: IGetTrxData | undefined,
  setDetailRow: React.Dispatch<React.SetStateAction<IGetTrxData | undefined>>,
  detail: IGetDetailTrxResponse | undefined,
  setDetail: React.Dispatch<React.SetStateAction<IGetDetailTrxResponse | undefined>>,
  useCustomHook: ReturnType<typeof useCreateReceiptSubmission>,
  fetchDetail: (id: string, extendBm: string) => Promise<void>,
}

const ReceiptSubmissionApprovalModal = ({
  isApprovalModal,
  setApprovalModal,
  detailRow,
  setDetailRow,
  detail,
  setDetail,
  useCustomHook,
  fetchDetail,
}: ReceiptSubmissionApprovalModalI) => {
  const { userInfo } = useAuth();
  const { refetchTable, isLoading, setLoading } = useCustomHook;
  const snackbar = useSnackbar();
  const [isOpen, setOpen] = useState(!!isApprovalModal);
  const [reasonState, setReasonState] = useState({
    approve: { value: "", error: "" },
    reject: { value: "", error: "" },
  });
  const [allArea, setAllArea] = useState<IAreaData[]>([]);

  const { data: dataAreas } = useQuery({
    queryKey: "get_areas",
    queryFn: getAreas,
  });

  useEffect(() => {
    if (dataAreas) setAllArea(dataAreas.listareaall);
  }, [dataAreas]);

  useEffect(() => {
    if (!isOpen) {
      setApprovalModal(false);
      setReasonState({ approve: { value: "", error: "" }, reject: { value: "", error: "" } });
    }
  }, [isOpen]);
  useEffect(() => {
    setOpen(!!isApprovalModal);
  }, [isApprovalModal]);

  const isApproveType = (
    isApprovalModal === ApprovalModalType.APPROVE
    || isApprovalModal === ApprovalModalType.DETAIL_APPROVE
  );
  const isRejectType = (
    isApprovalModal === ApprovalModalType.REJECT
    || isApprovalModal === ApprovalModalType.DETAIL_REJECT
  );

  const reasonKey = isApproveType ? "approve" as const : "reject" as const;
  const reasonLabel = isApproveType ? "setujui" : "tolak";
  const currentReason = reasonState[reasonKey];

  const handleReasonChange = (value: string) => {
    setReasonState((prev) => ({ ...prev, [reasonKey]: { value, error: "" } }));
  };

  const handleApprove = async () => {
    if ((isApproveType || isRejectType) && !currentReason.value) {
      setReasonState((prev) => ({
        ...prev,
        [reasonKey]: { ...prev[reasonKey], error: `Alasan ${reasonLabel} wajib diisi` },
      }));
      return;
    }
    setLoading(true);
    if (detail) {
      putTrxApproval({
        trx_id: detail.trx_id,
        approval_number: detail.trx_approvals.find((approval) => approval.status_approval === "WAITING")?.approval_number.toString() || "0",
        status_approval: (isApproveType ? "APPROVED" : "REJECTED"),
        employee_email: userInfo.email,
        reason_status: currentReason.value,
        update_by: userInfo.preferred_username,
      }).then((res) => {
        if (res.responseCode === "Succeed") {
          const currDetailRow = { ...detailRow };
          refetchTable();
          snackbar.show(`Pengajuan bon telah ${isApproveType ? "disetujui" : "ditolak"}`, "success");
          setOpen(false);
          if ((
            isApprovalModal === ApprovalModalType.DETAIL_APPROVE
            || isApprovalModal === ApprovalModalType.DETAIL_REJECT
          ) && currDetailRow.trx_id) {
            fetchDetail(currDetailRow.trx_id, currDetailRow.extend_bm || "0");
          } else {
            setDetailRow(undefined);
            setLoading(false);
          }
        } else {
          if (detailRow) fetchDetail(detailRow.trx_id, detailRow.extend_bm);
          snackbar.show(res.responseMessage || `Pengajuan bon gagal ${isApproveType ? "disetujui" : "ditolak"}`, "error");
        }
      }).catch(() => {
        setLoading(false);
        snackbar.show(`Pengajuan bon gagal ${isApproveType ? "disetujui" : "ditolak"}`, "error");
      });
    } else {
      setLoading(false);
      snackbar.show("Pengajuan bon gagal menampilkan detail", "error");
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    if (detailRow) {
      deleteTrx(detailRow.trx_id).then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          snackbar.show("Berhasil menghapus pengajuan bon", "success");
          setOpen(false);
          setDetailRow(undefined);
        } else {
          snackbar.show(res.responseMessage, "error");
        }
      }).catch(() => {
        snackbar.show("Gagal menghapus pengajuan bon", "error");
      }).finally(() => setLoading(false));
    }
  };

  const generateTitle = () => {
    let text = "";
    switch (isApprovalModal) {
      case ApprovalModalType.APPROVE:
      case ApprovalModalType.DETAIL_APPROVE:
        text = "Persetujuan pengajuan bon";
        break;
      case ApprovalModalType.REJECT:
      case ApprovalModalType.DETAIL_REJECT:
        text = "Penolakan pengajuan bon";
        break;
      case ApprovalModalType.DELETE:
        text = "Konfirmasi hapus data";
        break;

      default:
        break;
    }
    return text;
  };

  return (
    <Modal
      isOpen={isOpen}
      setOpen={setOpen}
      title={generateTitle()}
      footer={<>
        <Button
          data-testid="modal-confirm-cancel"
          type="button"
          variant="text"
          onClick={() => setOpen(false)}
          disabled={isLoading}
        >
          <Typography level={5} color={primary[500]} fontWeight="bold" >
            Batal
          </Typography>
        </Button>
        {isApproveType
          && <Button
            data-testid="modal-confirm-submit"
            type="submit"
            backgroundColor={currentReason.value ? primary[400] : undefined}
            onClick={handleApprove}
            disabled={isLoading || !currentReason.value}
            leftIcon={isLoading && <Loading variant="circular" />}
          >
            <Typography level={5} color="white" fontWeight="bold">
              Setujui
            </Typography>
          </Button>
        }
        {isRejectType
          && <Button
            data-testid="modal-confirm-submit"
            type="submit"
            backgroundColor={currentReason.value ? danger[400] : undefined}
            onClick={handleApprove}
            disabled={isLoading || !currentReason.value}
            leftIcon={isLoading && <Loading variant="circular" />}
          >
            <Typography level={5} color="white" fontWeight="bold">
              Tolak
            </Typography>
          </Button>
        }
        {isApprovalModal === ApprovalModalType.DELETE
          && <Button
            data-testid="modal-confirm-submit"
            type="submit"
            backgroundColor={danger[400]}
            onClick={handleDelete}
            disabled={isLoading}
            leftIcon={isLoading && <Loading variant="circular" />}
          >
            <Typography level={5} color="white" fontWeight="bold">
              Ya, hapus
            </Typography>
          </Button>
        }
      </>}
      body={
        <div style={{ margin: "-16px", overflowY: "auto", maxHeight: "70vh" }}>
          {detail
            ? <>
              {isApprovalModal === ApprovalModalType.APPROVE && <>
                <div style={{ display: "grid", gap: 24, padding: 24 }}>
                  <Typography level={6} fontWeight="medium">Detail pemohon</Typography>
                  <div style={{ display: "grid", gridTemplateColumns: "192px 16px auto", gap: 16 }}>
                    <LabelAndValue label="Nama & NIP" value={<>{detail.employee_name} • <Typography level={6} fontWeight="bold" color={primary[400]}>{detail.employee_nip}</Typography></>} />
                    <LabelAndValue label="Wilayah" value={allArea.find((area) => area.id === detail.area_id)?.name} />
                    <LabelAndValue label="Pool" value={detail.pool_id} />
                    <LabelAndValue label="Segmen" value={detail.segment.code_description} />
                    <LabelAndValue label="Kode perusahaan" value={detail.company_code} />
                    <LabelAndValue label="Perusahaan relasi" value={detail.company_relation || "-"} />
                  </div>
                </div>
                <Divider color={secondary[200]} />
                <div style={{ display: "grid", gap: 24, padding: 24 }}>
                  <Typography level={6} fontWeight="medium">Detail pengajuan</Typography>
                  <div style={{ display: "grid", gridTemplateColumns: "192px 16px auto", gap: 16 }}>
                    <LabelAndValue label="No. pengajuan bon" value={detail.trx_id} />
                    <LabelAndValue label="Tipe transaksi" value={detail.trx_type.description} />
                    {detail.trx_type.trx_type === "BMH" && detail.detail_bon_merah?.bon_merah_type
                      ? <LabelAndValue label="Tipe bon merah" value={detail.detail_bon_merah.bon_merah_type} />
                      : <></>
                    }
                    <LabelAndValue label="Tanggal pengajuan" value={toStringDate(detail.trx_date)} />
                    <LabelAndValue label="Akun GL" value={<>
                      {detail.gnlldr}
                      &nbsp;•&nbsp;
                      {CommonTrxTypes.find(
                        (type) => type.value === detail.sap_shkzg,
                      )?.label}
                      <br />
                      {detail.trx_desc}
                    </>} />
                    <LabelAndValue label="Total bon" value={toRupiah(detail.amount)} />
                    <LabelAndValue label="Rekening tujuan" value={`${detail.bank_account} • ${detail.bank_account_name}`} />
                    <LabelAndValue label="Deskripsi" value={isHtmlString(detail.trx_text)
                      ? (<DescriptionCol
                        dangerouslySetInnerHTML={{ __html: detail.trx_text }}
                      />)
                      : (<DescriptionCol>
                        {detail.trx_text}
                      </DescriptionCol>)
                    } />
                  </div>
                </div>
                <Divider color={secondary[200]} />
                <div style={{ display: "grid", gap: 24, padding: 24 }}>
                  <Typography level={6} fontWeight="medium">Lampiran</Typography>
                  <FileWrapper>
                    {detail.trx_files.map((doc) => (
                      <FileItem onClick={async () => window.open(await getDocUrl(doc))}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {doc.file_name.split(".").pop() === "pdf" ? <PdfIcon /> : <ImgIcon />}
                        </div>
                        <div className="ellipsis-text">
                          {doc.file_name}
                          <div>
                            <Typography level={7} fontWeight="medium">{byteStringToFileSize(doc.file_size)}</Typography>
                          </div>
                        </div>
                        <div style={{ height: 21.6, display: "flex", alignItems: "center" }}>
                          <ShowIcon style={{ color: gray.disable }} />
                        </div>
                      </FileItem>
                    ))}
                  </FileWrapper>
                </div>
                <div style={{
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "white",
                  padding: "16px 24px",
                  zIndex: 1,
                }}>
                  <FormGroup>
                    <FormWrapper>
                      <Form
                        data-testid="approve-reason-input"
                        label="Alasan setujui"
                        required
                        value={currentReason.value}
                        onChange={(e) => handleReasonChange(e.target.value.trimStart())}
                        maxLength={100}
                      />
                    </FormWrapper>
                  </FormGroup>
                </div>
              </>
              }
              {isApprovalModal === ApprovalModalType.DETAIL_APPROVE && <div style={{
                display: "grid", gap: 16, padding: "24px",
              }}>
                <div>
                  <Typography level={5} fontWeight="bold" color={gray.subtitle}>Setujui pengajuan bon dari </Typography>
                  <Typography level={5} fontWeight="bold" color={primary[500]}>{detail.employee_name} • {detail.employee_nip}?</Typography>
                </div>
                <FormGroup>
                  <FormWrapper>
                    <Form
                      data-testid="approve-reason-input-detail"
                      label="Alasan setujui"
                      required
                      value={currentReason.value}
                      onChange={(e) => handleReasonChange(e.target.value.trimStart())}
                      maxLength={100}
                    />
                  </FormWrapper>
                </FormGroup>
              </div>
              }
              {isRejectType && <div style={{
                display: "grid", gap: 16, padding: "24px",
              }}>
                <FormGroup>
                  <FormWrapper>
                    <Form
                      data-testid="reject-reason-input"
                      label="Alasan tolak"
                      value={currentReason.value}
                      onChange={(e) => handleReasonChange(e.target.value.trimStart())}
                      maxLength={100}
                      required
                    />
                  </FormWrapper>
                </FormGroup>
              </div>
              }
              {isApprovalModal === ApprovalModalType.DELETE && <div style={{
                display: "flex", flexDirection: "column", gap: 24, padding: "24px",
              }}>
                <Typography level={5}>
                  Apakah Anda yakin ingin menghapus data pengajuan bon dari&nbsp;
                  <Typography level={5} fontWeight="medium">
                    {detail.employee_name} • {detail.employee_nip}&nbsp;
                  </Typography>
                  ?
                </Typography>
                <Divider color={gray.line} />
                <div style={{ display: "grid", gridTemplateColumns: "192px 16px auto", rowGap: 16 }}>
                  <LabelAndValue label="Tanggal pengajuan" value={toStringDate(detail.trx_date)} />
                  <LabelAndValue label="No. pengajuan bon" value={detail.trx_id} />
                  <LabelAndValue label="Tipe transaksi" value={detail.trx_type.description} />
                  <LabelAndValue label="Total bon" value={toRupiah(detail.amount)} />
                  <LabelAndValue label="Akun GL" value={<>
                    {detail.gnlldr}
                    &nbsp;•&nbsp;
                    {CommonTrxTypes.find(
                      (type) => type.value === detail.sap_shkzg,
                    )?.label}
                    <br />
                    {detail.trx_desc}
                  </>} />
                  <LabelAndValue label="Rekening tujuan" value={`${detail.bank_account} • ${detail.bank_account_name}`} />
                  <LabelAndValue label="Deskripsi" value={detail.trx_desc} />
                </div>
              </div>
              }
            </>
            : <Loading variant="circular" />
          }
        </div>
      }
    />
  );
};

export default ReceiptSubmissionApprovalModal;
